// src/shared/components/ui/Dialog/SenderDetailsDialogWrapper.tsx
import React, { useState } from 'react';
import { useNotificationStore } from '../../../../app/store/useNotificationStore';
import SenderDetailsDialog from './SenderDetailsDialog';
import OfferConfirmationDialog from './OfferConfirmationDialog';
import EditCartRequestDialog from './EditCartRequestDialog';
import EditCartResultDialog from './EditCartResultDialog';
import { DeclineOfferDialog } from './DeclineOfferDialog';
import { useGetShipmentDetailsQuery } from '../../../../api/senderApi2';
import { useAcceptOfferRequestMutation, useRejectOfferRequestMutation } from '../../../../api/senderApi3';
import { useGetMyTravelItineraryDetailQuery, useApproveEditItemRequestMutation, useCancelEditItemRequestMutation } from '../../../../api/travellerApi2';
import { useAcceptOfferRequestMutation as useTravellerAcceptOfferRequestMutation, useRejectOfferRequestMutation as useTravellerRejectOfferRequestMutation } from '../../../../api/travellerApi3';
import { notificationStorage } from '../../../utils/notificationStorage';
import { useMarkNotificationAsRead } from '../../../../api/NotificationApi';
import { useDispatch } from 'react-redux';
import { markAsRead } from '../../../../app/store/slices/notificationSlice';
import { useUserStore } from "../../../../app/store/useUserStore";
import { isTravellerRole } from "../../../utils/roleUtils";
import { useQueryClient } from '@tanstack/react-query';
import { useToastStore } from '../../../../app/store/useToastStore';
import { CURRENCY_CODE, CURRENCY_SYMBOL } from '../../../styles/theme/theme';
import { KycVerificationDialog } from './KycVerificationDialog';
import { useKycVerification } from '../../../hooks/useKycVerification';
import { useGetAirportsQuery } from '../../../../api/userApi';


export const SenderDetailsDialogWrapper: React.FC = () => {
  const {
    selectedNotification,
    detailsDialogOpen,
    closeDetailsDialog,
    setNotificationActionLoading,
  } = useNotificationStore();

  const { user } = useUserStore();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();

  const { isFullyVerified } = useKycVerification();
  const [showKycDialog, setShowKycDialog] = useState(false);

  // Local state for confirmation dialogs
  const [showAcceptanceDialog, setShowAcceptanceDialog] = useState(false);
  const [showRejectionDialog, setShowRejectionDialog] = useState(false);
  const [showDeclineOfferDialog, setShowDeclineOfferDialog] = useState(false);
  const [showEditApprovedDialog, setShowEditApprovedDialog] = useState(false);
  const [showEditRejectedDialog, setShowEditRejectedDialog] = useState(false);

  // Store order code and user type for decline dialog (to preserve after closing details dialog)
  const [pendingRejectData, setPendingRejectData] = useState<{
    orderCode: string;
    isTraveler: boolean;
    notificationId: string;
    backendNotificationId?: string;
  } | null>(null);

  // API mutations
  const senderAcceptMutation = useAcceptOfferRequestMutation();
  const senderRejectMutation = useRejectOfferRequestMutation();
  const travellerAcceptMutation = useTravellerAcceptOfferRequestMutation();
  const markNotificationAsReadMutation = useMarkNotificationAsRead();
  const travellerRejectMutation = useTravellerRejectOfferRequestMutation();
  const approveEditItemMutation = useApproveEditItemRequestMutation();
  const cancelEditItemMutation = useCancelEditItemRequestMutation();

  const isTraveler = isTravellerRole(user?.user_role);

  const shipmentId = selectedNotification?.data?.shipment_id;
  const { data: shipmentData, isLoading: isLoadingShipment } = useGetShipmentDetailsQuery(String(shipmentId), {
    enabled: !!shipmentId && detailsDialogOpen && isTraveler,
  });

  const itineraryId = selectedNotification?.data?.itinerary_id;
  const { data: itineraryData, isLoading: isLoadingItinerary } = useGetMyTravelItineraryDetailQuery(
    itineraryId ? itineraryId : "0",
    {
      enabled: !!itineraryId && detailsDialogOpen && !isTraveler,
    }
  );

  const viewingUserCurrencyCode = CURRENCY_CODE;
  const viewingUserCurrencySymbol = CURRENCY_SYMBOL;

  // Resolve airport names to search for IATA codes
  // departure_airport is a plain string from the API, not an object
  const senderDepartureAirportName = shipmentData?.data?.departure_airport || '';
  const senderDestinationAirportName = shipmentData?.data?.destination_airport || '';
  const travelerDepartureAirportName = itineraryData?.departure_airport || '';
  const travelerDestinationAirportName = itineraryData?.destination_airport || '';

  const departureSearchName = isTraveler ? senderDepartureAirportName : travelerDepartureAirportName;
  const destinationSearchName = isTraveler ? senderDestinationAirportName : travelerDestinationAirportName;

  const { data: departureAirportResults } = useGetAirportsQuery(departureSearchName, {
    enabled: !!departureSearchName && detailsDialogOpen,
  });
  const { data: destinationAirportResults } = useGetAirportsQuery(destinationSearchName, {
    enabled: !!destinationSearchName && detailsDialogOpen,
  });

  const findIata = (results: { name: string; iata: string }[] | undefined, airportName: string) => {
    if (!results || !airportName) return '';
    const match = results.find(
      (a) => a.name.toLowerCase() === airportName.toLowerCase()
    );
    return match?.iata || '';
  };

  const findCountryCode = (results: { name: string; country_code?: string }[] | undefined, airportName: string) => {
    if (!results || !airportName) return '';
    const match = results.find(
      (a) => a.name.toLowerCase() === airportName.toLowerCase()
    );
    return match?.country_code || '';
  };

  const notificationAllocatedPrice = selectedNotification?.data?.allocated_price != null
    ? Number(selectedNotification.data.allocated_price)
    : undefined;

  const notificationSenderPlatformFeePercentage = selectedNotification?.data?.sender_platform_fee_percentage != null
    ? Number(selectedNotification.data.sender_platform_fee_percentage)
    : undefined;
  const notificationTravelerPlatformFeePercentage = selectedNotification?.data?.traveler_platform_fee_percentage != null
    ? Number(selectedNotification.data.traveler_platform_fee_percentage)
    : undefined;

  const senderData = shipmentData?.data ? (() => {
    const priceUSD = Number(shipmentData.data.total_price || 0);
    const convertedPrice = priceUSD;
    const convertedAllocatedPrice = notificationAllocatedPrice != null
      ? notificationAllocatedPrice
      : undefined;

    const depCode = shipmentData.data.departure_airport_code
      || findIata(departureAirportResults, senderDepartureAirportName);
    const destCode = shipmentData.data.destination_airport_code
      || findIata(destinationAirportResults, senderDestinationAirportName);
    const depCountryCode = shipmentData.data.departure_country_code
      || findCountryCode(departureAirportResults, senderDepartureAirportName);
    const destCountryCode = shipmentData.data.destination_country_code
      || findCountryCode(destinationAirportResults, senderDestinationAirportName);

    return {
      id: shipmentData.data.id,
      shipmentId: shipmentData.data.id,
      itineraryId: itineraryId,
      name: shipmentData.data.user?.full_name || shipmentData.data.user?.name,
      profileImageUrl: shipmentData.data.user?.profileImageUrl || shipmentData.data.user?.profile_image,
      rating: shipmentData.data?.rating || 0,
      origin: senderDepartureAirportName,
      departure_airport_code: depCode,
      departure_country_code: depCountryCode,
      destination: senderDestinationAirportName,
      destination_airport_code: destCode,
      destination_country_code: destCountryCode,
      weight: shipmentData.data.total_weight,
      category: shipmentData.data.items?.map((item: unknown) => item.category).filter(Boolean).join(', '),
      shipping_start_date: shipmentData.data.shipping_start_date,
      shipping_end_date: shipmentData.data.shipping_end_date,
      price: convertedPrice,
      allocatedPrice: convertedAllocatedPrice,
      shipping_time: shipmentData.data.shipping_time,
      items: shipmentData.data.items,
      images: shipmentData.data.images,
      currencyCode: viewingUserCurrencyCode,
      currencySymbol: viewingUserCurrencySymbol,
      description: shipmentData.data.description,
      senderPlatformFeePercentage: notificationSenderPlatformFeePercentage,
      travelerPlatformFeePercentage: notificationTravelerPlatformFeePercentage,
    };
  })() : null;

  const travelerDepCode = itineraryData?.departure_airport_code
    || findIata(departureAirportResults, travelerDepartureAirportName);
  const travelerDestCode = itineraryData?.destination_airport_code
    || findIata(destinationAirportResults, travelerDestinationAirportName);
  const travelerDepCountryCode = itineraryData?.departure_country_code
    || findCountryCode(departureAirportResults, travelerDepartureAirportName);
  const travelerDestCountryCode = itineraryData?.destination_country_code
    || findCountryCode(destinationAirportResults, travelerDestinationAirportName);

  const travelerData = itineraryData ? {
    shipmentId: shipmentId,
    itineraryId: itineraryId,
    name: itineraryData.name || selectedNotification?.message?.split(" has")[0] || '',
    profileImageUrl: itineraryData.profileImageUrl,
    rating: itineraryData.rating || 0,
    origin: travelerDepartureAirportName,
    departure_airport_code: travelerDepCode,
    departure_country_code: travelerDepCountryCode,
    departure_date: itineraryData.departure_date,
    destination: travelerDestinationAirportName,
    destination_airport_code: travelerDestCode,
    destination_country_code: travelerDestCountryCode,
    arrival_date: itineraryData.arrival_date,
    weight: parseFloat(itineraryData.weight || '0'),
    price: parseFloat(itineraryData.price || '0'),
    allocatedPrice: notificationAllocatedPrice != null
      ? notificationAllocatedPrice
      : undefined,
    weightType: itineraryData.type || 'kg',
    shipping_time: itineraryData.shipping_time,
    categories: Array.isArray(itineraryData.categories)
      ? itineraryData.categories.map((c: any) => c?.category_name || c?.category || c?.name || '').filter(Boolean)
      : [],
    senderPlatformFeePercentage: notificationSenderPlatformFeePercentage,
    travelerPlatformFeePercentage: notificationTravelerPlatformFeePercentage,
  } : null;

  const isLoading = isTraveler ? isLoadingShipment : isLoadingItinerary;

  const handleAccept = async () => {
    if (!selectedNotification) return;

    if (!isFullyVerified) {
      closeDetailsDialog();
      setShowKycDialog(true);
      return;
    }

    try {
      setNotificationActionLoading(true);
      const orderCode = selectedNotification.data?.order_code;

      if (!orderCode) {
        return;
      }

      if (isTraveler) {
        await travellerAcceptMutation.mutateAsync({
          order_code: orderCode,
        });
      } else {
        const response = await senderAcceptMutation.mutateAsync({
          order_code: orderCode,
          success_url: `${import.meta.env.VITE_APP_BASE_URL_Stripe}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${import.meta.env.VITE_APP_BASE_URL_Stripe}/payment/cancel`,
        });

        closeDetailsDialog();

        // Mark notification done only after Stripe success
        if (response?.stripe_session?.url) {
          localStorage.setItem('pending_notification_id', selectedNotification.id);
          localStorage.setItem('pending_offer_order_code', String(orderCode));
          if (selectedNotification.data?.notification_id) {
            localStorage.setItem('pending_backend_notification_id', String(selectedNotification.data.notification_id));
          }
          window.location.href = response.stripe_session.url;
          return;
        }

        return;
      }

      const notifications = notificationStorage.getNotifications();
      const notif = notifications.find((n) => n.id === selectedNotification.id);

      if (notif) {
        notif.isRead = true;
        notif.data = {
          ...notif.data,
          statusFlag: 'done',
        };

        // Save updated notifications
        const updated = notifications.map((n) => (n.id === selectedNotification.id ? notif : n));
        localStorage.setItem('skyporter_notifications', JSON.stringify(updated));

        // Dispatch state change event
        window.dispatchEvent(new CustomEvent('notification-state-changed'));

      }

      dispatch(markAsRead(selectedNotification.id));

      // Mark as read on backend
      if (selectedNotification.data?.notification_id) {
        markNotificationAsReadMutation.mutate(selectedNotification.data.notification_id);
      }

      closeDetailsDialog();
      setShowAcceptanceDialog(true);
    } catch (error: unknown) {
      closeDetailsDialog();
    } finally {
      setNotificationActionLoading(false);
    }
  };

  const handleReject = () => {
    if (!selectedNotification) return;

    if (!isFullyVerified) {
      closeDetailsDialog();
      setShowKycDialog(true);
      return;
    }

    const orderCode = selectedNotification.data?.order_code;
    if (!orderCode) return;
    

    setPendingRejectData({
      orderCode,
      isTraveler,
      notificationId: selectedNotification.id,
      backendNotificationId: selectedNotification.data?.notification_id,
    });

    closeDetailsDialog();
    setShowDeclineOfferDialog(true);
  };

  const handleDeclineOfferSubmit = async (reason: string) => {
    if (!pendingRejectData) {
      return;
    }

    const { orderCode, isTraveler: isUserTraveler, notificationId, backendNotificationId } = pendingRejectData;

    try {
      setNotificationActionLoading(true);

      if (isUserTraveler) {
        await travellerRejectMutation.mutateAsync({
          order_code: orderCode,
          reason: reason,
        });
      } else {
        await senderRejectMutation.mutateAsync({
          order_code: orderCode,
          reason: reason,
        });
      }

      const notifications = notificationStorage.getNotifications();
      const notif = notifications.find((n) => n.id === notificationId);

      if (notif) {
        notif.isRead = true;
        notif.data = {
          ...notif.data,
          statusFlag: 'rejected',
        };

        const updated = notifications.map((n) => (n.id === notificationId ? notif : n));
        localStorage.setItem('skyporter_notifications', JSON.stringify(updated));

        window.dispatchEvent(new CustomEvent('notification-state-changed'));

      }

      dispatch(markAsRead(notificationId));

      // Mark as read on backend
      if (backendNotificationId) {
        markNotificationAsReadMutation.mutate(backendNotificationId);
      }

      setPendingRejectData(null);
      setShowDeclineOfferDialog(false);
      queryClient.invalidateQueries();
      setShowRejectionDialog(true);
    } catch (error: unknown) {
      setShowDeclineOfferDialog(false);
      setPendingRejectData(null);
    } finally {
      setNotificationActionLoading(false);
    }
  };

  const markNotificationAsDone = (notificationId: string) => {
    const notifications = notificationStorage.getNotifications();
    const notif = notifications.find((n) => n.id === notificationId);

    if (notif) {
      notif.isRead = true;
      notif.data = {
        ...notif.data,
        statusFlag: 'done',
      };

      const updated = notifications.map((n) => (n.id === notificationId ? notif : n));
      localStorage.setItem('skyporter_notifications', JSON.stringify(updated));

      window.dispatchEvent(new CustomEvent('notification-state-changed'));

    }
  };

  const handleApproveEditRequest = async () => {
    if (!selectedNotification) return;

    try {
      const items = selectedNotification.data?.items;
      const itemId = selectedNotification.data?.item_id || items?.[0]?.id;
      const orderCode = selectedNotification.data?.order_code || selectedNotification.data?.orderCode;

      if (!itemId || !orderCode) {
        showToast('Missing item or order information. Please try again.', 'error');
        return;
      }

      await approveEditItemMutation.mutateAsync({
        item_id: itemId,
        order_code: orderCode,
      });

      markNotificationAsDone(selectedNotification.id);
      closeDetailsDialog();
      queryClient.invalidateQueries();
      setShowEditApprovedDialog(true);
    } catch (error: unknown) {
    }
  };

  const handleRejectEditRequest = async () => {
    if (!selectedNotification) return;

    try {
      const items = selectedNotification.data?.items;
      const itemId = selectedNotification.data?.item_id || items?.[0]?.id;
      const orderCode = selectedNotification.data?.order_code || selectedNotification.data?.orderCode;

      if (!itemId || !orderCode) {
        showToast('Missing item or order information. Please try again.', 'error');
        return;
      }

      await cancelEditItemMutation.mutateAsync({
        item_id: itemId,
        order_code: orderCode,
      });

      markNotificationAsDone(selectedNotification.id);
      closeDetailsDialog();
      queryClient.invalidateQueries();
      setShowEditRejectedDialog(true);
    } catch (error: unknown) {
    }
  };

  const isEditCartRequest = selectedNotification?.type === 'edit_cart_request';

  return (
    <>
      {isEditCartRequest ? (() => {
        const items = selectedNotification?.data?.items;
        const itemId = selectedNotification?.data?.item_id;
        const item = items?.find((i: unknown) => i.id === itemId) || items?.[0];
        const orderCode = selectedNotification?.data?.order_code || selectedNotification?.data?.orderCode;
        const senderName = selectedNotification?.data?.sender_name || selectedNotification?.data?.name;

        return (
          <EditCartRequestDialog
            open={detailsDialogOpen}
            onClose={closeDetailsDialog}
            onApprove={handleApproveEditRequest}
            onReject={handleRejectEditRequest}
            editDetails={{
              item_id: itemId || item?.id,
              order_code: orderCode,
              senderName: senderName,
              oldValues: {
                category: selectedNotification?.data?.old_category || item?.category,
                weight: selectedNotification?.data?.old_weight || item?.weight,
                price: selectedNotification?.data?.old_price || item?.price,
                pieces: selectedNotification?.data?.old_pieces || item?.pieces,
                pages: selectedNotification?.data?.old_pages || item?.pages,
              },
              newValues: {
                category: selectedNotification?.data?.new_category || item?.category,
                weight: selectedNotification?.data?.new_weight || item?.edit_weight,
                price: selectedNotification?.data?.new_price || item?.edit_price,
                pieces: selectedNotification?.data?.new_pieces || item?.edit_pieces,
                pages: selectedNotification?.data?.new_pages || item?.edit_pages,
              },
            }}
            isLoading={approveEditItemMutation.isPending || cancelEditItemMutation.isPending}
          />
        );
      })() : (
        <SenderDetailsDialog
          open={detailsDialogOpen}
          onClose={closeDetailsDialog}
          onAccept={handleAccept}
          onReject={handleReject}
          sender={isTraveler ? senderData : travelerData}
          itineraryId={!isTraveler ? itineraryId : undefined}
          mode={isTraveler ? "sender" : "traveller"}
          isLoading={isLoading}
          isAccepting={senderAcceptMutation.isPending || travellerAcceptMutation.isPending}
        />
      )}

      <OfferConfirmationDialog
        open={!isTraveler && showAcceptanceDialog}
        onClose={() => setShowAcceptanceDialog(false)}
        variant="accepted"
        travelerName={(() => {
          // Extract traveler name from notification message (first 2 words)
          const msg = selectedNotification?.message || '';
          const words = msg.trim().split(/\s+/);
          const nameFromMessage = words.length >= 2 ? `${words[0]} ${words[1]}` : words[0] || '';
          return nameFromMessage || String(senderData?.name || selectedNotification?.data?.sender_name || selectedNotification?.data?.name || '');
        })()}
        itineraryName={String(
          senderData?.category ||
          selectedNotification?.data?.item_name ||
          selectedNotification?.data?.category_name ||
          itineraryData?.name ||
          selectedNotification?.data?.itinerary_name ||
          selectedNotification?.data?.itineraryName ||
          ''
        )}
      />

      <OfferConfirmationDialog
        open={showRejectionDialog}
        onClose={() => setShowRejectionDialog(false)}
        variant="rejected"
        onViewTravelers={() => {
          setShowRejectionDialog(false);
          // Navigate to travelers page or close dialog
        }}
      />

      {/* Edit Cart Approved Dialog */}
      <EditCartResultDialog
        open={showEditApprovedDialog}
        onClose={() => setShowEditApprovedDialog(false)}
        variant="approved"
        onViewDetails={() => {
          setShowEditApprovedDialog(false);
          // Navigate to order details if needed
        }}
        orderDetails={{
          orderCode: selectedNotification?.data?.order_code,
          senderName: selectedNotification?.data?.sender_name,
        }}
      />

      {/* Edit Cart Rejected Dialog */}
      <EditCartResultDialog
        open={showEditRejectedDialog}
        onClose={() => setShowEditRejectedDialog(false)}
        variant="rejected"
        orderDetails={{
          orderCode: selectedNotification?.data?.order_code,
          senderName: selectedNotification?.data?.sender_name,
        }}
      />

      <KycVerificationDialog
        isOpen={showKycDialog}
        onClose={() => setShowKycDialog(false)}
      />

      {/* Decline Offer Dialog - For rejecting offers from notification */}
      <DeclineOfferDialog
        open={showDeclineOfferDialog}
        onClose={() => {
          setShowDeclineOfferDialog(false);
          setPendingRejectData(null);
        }}
        onSubmit={handleDeclineOfferSubmit}
        isLoading={travellerRejectMutation.isPending || senderRejectMutation.isPending}
        userType={pendingRejectData?.isTraveler ? 'traveler' : 'sender'}
      />
    </>
  );
};